/**
 * /settings/pocket — operational dashboard for the Pocket QR/Universal-
 * Link pipeline.
 *
 * Layout matches the rest of the settings pages:
 *   - Standard h1 + subtitle header
 *   - Tabs for primary navigation (Setup / Performance / Reliability /
 *     Fleet) — keeps each surface compact and uses horizontal real
 *     estate for tab nav rather than sidebar prose.
 *   - Within each tab, a responsive grid lays cards out in 1 / 2 / 3
 *     columns depending on viewport. Tight, dense, no chunky descriptor
 *     paragraphs — explanations live in title-row "?" tooltips.
 */

import { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Smartphone,
  Gauge,
  ShieldCheck,
  BarChart3,
  Activity,
  Loader2,
} from 'lucide-react';
import { getQrRollup, getAasaInfo } from '@/lib/pocketSettingsApi';
import type { PocketQrRollup, PocketAasaInfo } from '@/types/pocketSettings';
import { AasaPreviewCard } from '@/components/settings/pocket/AasaPreviewCard';
import { QrFleetMetricsCard } from '@/components/settings/pocket/QrFleetMetricsCard';
import { RecentRestoresList } from '@/components/settings/pocket/RecentRestoresList';
import { DashboardCacheCard } from '@/components/settings/pocket/DashboardCacheCard';
import { RateLimitOverridesCard } from '@/components/settings/pocket/RateLimitOverridesCard';
import { QrDefaultsCard } from '@/components/settings/pocket/QrDefaultsCard';
import { IdempotencyMonitorCard } from '@/components/settings/pocket/IdempotencyMonitorCard';
import { OutboxHealthCard } from '@/components/settings/pocket/OutboxHealthCard';
import { OutboxStalledCard } from '@/components/settings/pocket/OutboxStalledCard';
import { CronFailuresCard } from '@/components/settings/pocket/CronFailuresCard';
import { StalledRowDrawer } from '@/components/settings/pocket/StalledRowDrawer';
import type { StalledRow } from '@/types/pocketSettings';
import { HealthIndicatorsBar } from '@/components/settings/pocket/HealthIndicatorsBar';
import { FleetStatusComposite } from '@/components/settings/pocket/FleetStatusComposite';
import { FleetQrManager } from '@/components/settings/pocket/FleetQrManager';
import { PhotoCheckinConfigCard } from '@/components/settings/pocket/PhotoCheckinConfigCard';
import { DamagedReturnsCard } from '@/components/settings/pocket/DamagedReturnsCard';
import { SalesIntegrationCard } from '@/components/settings/pocket/SalesIntegrationCard';
import { TelemetryCard } from '@/components/settings/pocket/TelemetryCard';

const APP_DOMAIN = 'app.hollywoodtrucks.com';
const UNIVERSAL_LINK_PATTERN = `https://${APP_DOMAIN}/u/<unit_code>`;

export function SettingsPocket() {
  const [rollup, setRollup] = useState<PocketQrRollup | null>(null);
  const [aasa, setAasa] = useState<PocketAasaInfo | null>(null);
  const [loadingRollup, setLoadingRollup] = useState(true);
  // ISS-031: shared drawer state for stalled-row drill-down on the
  // Reliability tab. Hoisted to the page level so OutboxStalledCard +
  // CronFailuresCard can share a single drawer instance.
  const [stalledDrawerRow, setStalledDrawerRow] = useState<StalledRow | null>(null);
  const [stalledReloadKey, setStalledReloadKey] = useState(0);
  const [loadingAasa, setLoadingAasa] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getQrRollup()
      .then((data) => {
        if (!cancelled) setRollup(data);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load fleet rollup.");
      })
      .finally(() => {
        if (!cancelled) setLoadingRollup(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAasaInfo()
      .then((data) => {
        if (!cancelled) setAasa(data);
      })
      .catch(() => {
        // Non-fatal — surface inside the AASA card.
      })
      .finally(() => {
        if (!cancelled) setLoadingAasa(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    // Inner-scroll layout is desktop-only; on mobile the page flows so the
    // settings <main> handles vertical scrolling
    <div className="space-y-6 md:h-full md:overflow-hidden flex flex-col">
      {/* Header — matches the standard settings page pattern */}
      <div className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">
            Pocket
          </h1>
        </div>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
          Settings + diagnostics for the Pocket iPad app — QR codes, app links,
          performance, retries, and Sales-side sync.
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : null}

      <HealthIndicatorsBar />

      <FleetStatusComposite />

      <Tabs defaultValue="setup" className="flex-1 flex flex-col md:overflow-hidden">
        <TabsList className="bg-card border border-border p-1 w-fit">
          <TabsTrigger
            value="setup"
            className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
          >
            <Smartphone className="w-4 h-4" />
            Setup
          </TabsTrigger>
          <TabsTrigger
            value="performance"
            className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
          >
            <Gauge className="w-4 h-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger
            value="reliability"
            className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
          >
            <ShieldCheck className="w-4 h-4" />
            Reliability
          </TabsTrigger>
          <TabsTrigger
            value="fleet"
            className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
          >
            <BarChart3 className="w-4 h-4" />
            Fleet
          </TabsTrigger>
          <TabsTrigger
            value="telemetry"
            className="gap-2 data-[state=active]:bg-brand data-[state=active]:text-brand-foreground"
          >
            <Activity className="w-4 h-4" />
            Telemetry
          </TabsTrigger>
        </TabsList>

        {/* SETUP — Universal Link, AASA, QR generator defaults */}
        <TabsContent
          value="setup"
          className="flex-1 overflow-y-auto pt-4 space-y-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <UniversalLinkCard />
            {loadingAasa ? (
              <CardSkeleton title="apple-app-site-association" />
            ) : aasa ? (
              <AasaPreviewCard info={aasa} appDomain={APP_DOMAIN} />
            ) : null}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <QrDefaultsCard />
            <PhotoCheckinConfigCard />
            <DamagedReturnsCard />
          </div>
        </TabsContent>

        {/* PERFORMANCE — cache + rate limits */}
        <TabsContent
          value="performance"
          className="flex-1 overflow-y-auto pt-4 space-y-4"
        >
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <DashboardCacheCard />
            <RateLimitOverridesCard />
          </div>
        </TabsContent>

        {/* RELIABILITY — idempotency + outbox + Sales integration */}
        <TabsContent
          value="reliability"
          className="flex-1 overflow-y-auto pt-4 space-y-4"
        >
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <IdempotencyMonitorCard />
            <OutboxHealthCard />
          </div>
          <OutboxStalledCard
            onRowClick={setStalledDrawerRow}
            reloadKey={stalledReloadKey}
          />
          <CronFailuresCard
            onRowClick={setStalledDrawerRow}
            reloadKey={stalledReloadKey}
          />
          <SalesIntegrationCard />
        </TabsContent>

        {/* FLEET — QR coverage, per-unit grid + drawer, scan rate, restores */}
        <TabsContent
          value="fleet"
          className="flex-1 overflow-y-auto pt-4 space-y-4"
        >
          {loadingRollup ? (
            <CardSkeleton title="Fleet QR coverage" />
          ) : rollup ? (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="xl:col-span-2">
                  <QrFleetMetricsCard
                    fleet={rollup.fleet}
                    breakdown={rollup.active_breakdown}
                  />
                </div>
                <ScanRateCard rollup={rollup} />
              </div>

              {/* Per-unit grid + slide-in drawer for full management. */}
              <FleetQrManager />

              <RecentRestoresList entries={rollup.recent_restores} />
            </>
          ) : null}
        </TabsContent>

        {/* TELEMETRY — User-Agent capture + deferred toggles */}
        <TabsContent
          value="telemetry"
          className="flex-1 overflow-y-auto pt-4 space-y-4"
        >
          <TelemetryCard />
        </TabsContent>
      </Tabs>

      {/* ISS-031: shared drawer for stalled-row drill-down. Lives at the
          page level so OutboxStalledCard + CronFailuresCard (and any
          future siblings) can share a single drawer instance. */}
      <StalledRowDrawer
        row={stalledDrawerRow}
        open={stalledDrawerRow !== null}
        onClose={() => setStalledDrawerRow(null)}
        onMutated={() => setStalledReloadKey((k) => k + 1)}
      />
    </div>
  );
}

/**
 * Single-line Universal Link card — replaces the chunky descriptor paragraph.
 * Status badge + URL pattern + one-line context.
 */
function UniversalLinkCard() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <CardTitle className="text-base font-semibold">Universal Link</CardTitle>
        <Badge variant="secondary">Live</Badge>
      </CardHeader>
      <CardContent>
        <div className="font-mono text-sm break-all bg-muted/40 rounded-md px-3 py-2 mb-3">
          {UNIVERSAL_LINK_PATTERN}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Pattern every unit's QR sticker uses. iPads with Pocket installed
          intercept these via the AASA file; everyone else gets the public
          landing page.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Compact scan-rate card. Replaces the verbose paragraph from the previous
 * fleet section.
 */
function ScanRateCard({ rollup }: { rollup: PocketQrRollup }) {
  const r = rollup.scan_resolution_last_7d;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Old-sticker scans</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2 mb-2">
          <span
            className={
              'text-3xl font-semibold tabular-nums ' +
              (r.high_rate ? 'text-destructive' : 'text-foreground')
            }
          >
            {r.history_rate_pct}%
          </span>
          <span className="text-xs text-muted-foreground">last 7d</span>
        </div>
        {r.high_rate ? (
          <Badge
            variant="outline"
            className="border-destructive/40 text-destructive mb-3"
          >
            Above {rollup.historical_threshold_pct}% — investigate
          </Badge>
        ) : (
          <Badge variant="secondary" className="mb-3">
            Within threshold
          </Badge>
        )}
        <p className="text-xs text-muted-foreground">
          {r.resolved_via_history} of {r.total_scans} scans matched a
          retired/old QR. A small share is normal; sustained high means stickers
          in the field haven't been refreshed.
        </p>
      </CardContent>
    </Card>
  );
}

function CardSkeleton({ title }: { title: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      </CardContent>
    </Card>
  );
}
